import { useForm, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLocation, useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useRequestPasswordReset } from '../api'
import { useForgotPasswordEmailField } from '../hooks/useForgotPasswordEmailField'
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
  FORGOT_PASSWORD_EMAIL_STATE_KEY,
  readForgotPasswordEmailFromState,
} from '../lib'
import {
  extractMaskedEmail,
  isRegisteredEmailResponse,
} from '../lib/forgot-password-response'
import { BackToLoginButton } from '../components'
import {
  FORGOT_PASSWORD_CARD_FOOTER_SPACER_PX,
  FORGOT_PW_EMAIL_NOT_FOUND_MSG,
  forgotPasswordCardSx,
  forgotPasswordEmailErrorRowSx,
  forgotPasswordEmailFieldBlockSx,
  forgotPasswordEmailLabelSx,
  forgotPasswordErrorInputSx,
  forgotPasswordFieldErrorSx,
  forgotPasswordFormBlockSx,
  forgotPasswordHeadingFrameSx,
  forgotPasswordHeadingSx,
  forgotPasswordInputSx,
  forgotPasswordLogoBlockSx,
  forgotPasswordPrimaryButtonSx,
  forgotPasswordSubtitleSx,
  forgotPasswordTitleBlockSx,
} from '../styles/forgotPasswordStyles'

/** Figma 5074-11995 / 5074-12037 (input) / 5074-12040 (error). */
export function ForgotPasswordPage() {
  useDocumentTitle('Forgot Password')
  const navigate = useNavigate()
  const location = useLocation()
  const emailFromRoute = readForgotPasswordEmailFromState(location.state)
  const resetRequest = useRequestPasswordReset()

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: emailFromRoute ?? '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { handleSubmit } = form

  const {
    emailRegistration,
    hasEmailError,
    emailErrorMessage,
    setServerError,
    clearEmailErrors,
    handleMutationError,
    applySubmitValidationErrors,
  } = useForgotPasswordEmailField(form)

  const onValid = (data: ForgotPasswordFormData) => {
    resetRequest.mutate(
      { email: data.email },
      {
        onSuccess: (result) => {
          const maskedEmail = extractMaskedEmail(result)
          if (!isRegisteredEmailResponse(maskedEmail)) {
            setServerError(
              FORGOT_PW_EMAIL_NOT_FOUND_MSG,
              'not-found',
              data.email
            )
            return
          }
          posthog.capture('password_reset_requested')
          clearEmailErrors()
          navigate('/forgot-password/sent', {
            replace: true,
            state: { [FORGOT_PASSWORD_EMAIL_STATE_KEY]: data.email },
          })
        },
        onError: handleMutationError,
      }
    )
  }

  const onInvalid = (fieldErrors: FieldErrors<ForgotPasswordFormData>) => {
    applySubmitValidationErrors(fieldErrors.email?.message)
  }

  return (
    <Box data-testid="forgot-password-page" sx={forgotPasswordCardSx}>
      <Box sx={forgotPasswordLogoBlockSx}>
        <Box
          component="img"
          src={brushfireLogo}
          alt="Brushfire"
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <Box
        sx={forgotPasswordTitleBlockSx}
        data-testid="forgot-password-title-block"
      >
        <Box sx={forgotPasswordHeadingFrameSx}>
          <Typography component="h1" sx={forgotPasswordHeadingSx}>
            Reset Your Password
          </Typography>
        </Box>
        <Typography component="p" sx={forgotPasswordSubtitleSx}>
          Enter your email to receive a password reset link.
        </Typography>
      </Box>

      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onValid, onInvalid)}
        sx={{
          ...forgotPasswordFormBlockSx,
          gap: '24px',
          width: '100%',
        }}
      >
        <Box sx={forgotPasswordEmailFieldBlockSx}>
          <Typography
            component="label"
            htmlFor="forgot-email"
            sx={forgotPasswordEmailLabelSx}
          >
            Email
          </Typography>
          <Box data-testid="forgot-password-email-field">
            <OutlinedInput
              id="forgot-email"
              type="email"
              placeholder="Enter your email"
              fullWidth
              autoComplete="email"
              disabled={resetRequest.isPending}
              error={hasEmailError}
              aria-invalid={hasEmailError}
              aria-describedby={
                hasEmailError ? 'forgot-password-email-error' : undefined
              }
              sx={{
                ...forgotPasswordInputSx,
                ...(hasEmailError ? forgotPasswordErrorInputSx : {}),
              }}
              {...emailRegistration}
            />
          </Box>
        </Box>

        {hasEmailError && emailErrorMessage && (
          <Box sx={forgotPasswordEmailErrorRowSx} aria-live="polite">
            <Typography
              id="forgot-password-email-error"
              role="alert"
              data-testid="forgot-password-email-error"
              sx={forgotPasswordFieldErrorSx}
            >
              {emailErrorMessage}
            </Typography>
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disableElevation
          disabled={resetRequest.isPending}
          sx={forgotPasswordPrimaryButtonSx}
        >
          {resetRequest.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Send Email'
          )}
        </Button>

        <BackToLoginButton variant="outlined" />
      </Box>

      <Box
        aria-hidden
        sx={{
          height: FORGOT_PASSWORD_CARD_FOOTER_SPACER_PX,
          minHeight: FORGOT_PASSWORD_CARD_FOOTER_SPACER_PX,
          flexShrink: 0,
        }}
      />
    </Box>
  )
}
